import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useLoginMutation, useRequestPasswordResetMutation } from "@/api/authApi";
import { setCredentials } from "@/store/authSlice";
import { toast } from "sonner";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import type { RootState } from "@/store";
import loginHero from "@/assets/login-hero.png";
import logo from "@/assets/logo.svg";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export default function Login() {
  const [formData, setFormData] = useState({
    emailAddress: "",
    password: "",
  });
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState("");
  const [forgotPasswordOpen, setForgotPasswordOpen] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const dispatch = useDispatch();
  const { isAuthenticated, user } = useSelector((state: RootState) => state.auth);
  const navigate = useNavigate();
  const [login, { isLoading }] = useLoginMutation();
  const [requestPasswordReset, { isLoading: isResetLoading }] = useRequestPasswordResetMutation();

  useEffect(() => {
    if (isAuthenticated && user) {
      if (user.role === "SUPER_ADMIN" || user.role === "ADMIN" || user.role === "LAB_ADMIN") {
        navigate("/super-admin", { replace: true });
      } else if (user.role === "CLIENT" || user.role === "CLIENT_EMPLOYEE") {
        navigate("/client", { replace: true });
      }
    }
  }, [isAuthenticated, user, navigate]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    if (error) setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    try {
      const response = await login(formData).unwrap();
      dispatch(setCredentials({
        user: response.user,
        token: response.token
      }));
      toast.success("Login successful");
    } catch (err: any) {
      console.error("Login failed:", err);
      const message = err.data?.message || "Invalid email or password";
      setError(message);
      toast.error(message);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();

    const normalizedEmail = forgotPasswordEmail.trim().toLowerCase();
    if (!normalizedEmail) {
      toast.error("Please enter your email address");
      return;
    }

    try {
      const response = await requestPasswordReset({ email: normalizedEmail }).unwrap();
      toast.success(response.message || "Password reset email sent");
      setForgotPasswordOpen(false);
      setForgotPasswordEmail("");
    } catch (err: any) {
      toast.error(err?.data?.message || "Failed to send reset email");
    }
  };

  return (
    <div className="min-h-screen flex font-['Hind'] bg-white overflow-hidden">
      {/* Left Pane: Hero Image */}
      <div className=" hidden lg:block lg:w-1/2 relative bg-[#F5F8FA]">
        <img
          src={loginHero}
          alt="Healthcare Professional with Microscope"
          className="h-auto absolute inset-0 w-full h-full object-cover"
        />
      </div>

      {/* Right Pane: Login Form */}
      <div className="w-full lg:w-1/2 flex flex-col items-center justify-center">
        <div className="w-full max-w-[440px] flex flex-col items-center text-center">

          {/* Logo Section */}
          <div className="flex flex-col items-center ">
            <img src={logo} alt="MedXPro Health" className="h-auto object-contain" style={{ width: "23rem" }} />
          </div>

          <div className="space-y-3 mb-12 mt-20">
            <h1 className="text-[32px] font-semibold leading-[100%] text-[#012C4C]">
              Welcome Back!
            </h1>
            <p className="text-[18px] font-normal leading-[100%] text-[#7D7D7D]">
              Please enter your details to login
            </p>
          </div>

          <form onSubmit={handleSubmit} className="w-full space-y-6">
            <div className="space-y-2 text-left">
              <label className="text-[14px] font-semibold text-[#012C4C] ml-1">
                Email Address
              </label>
              <input
                name="emailAddress"
                type="email"
                placeholder="Enter your email address"
                value={formData.emailAddress}
                onChange={handleChange}
                className="w-full h-12 px-4 bg-white border border-[#012C4C] rounded-md text-[16px] text-[#012C4C] placeholder:text-[#012C4C]/40 focus:ring-0 focus:border-[#01889F] transition-colors"
                required
                disabled={isLoading}
              />
            </div>

            <div className="space-y-2 text-left">
              <label className="text-[14px] font-semibold text-[#012C4C] ml-1">
                Password
              </label>
              <div className="relative">
                <input
                  name="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter your password"
                  value={formData.password}
                  onChange={handleChange}
                  className="w-full h-12 px-4 pr-12 bg-white border border-[#012C4C] rounded-md text-[16px] text-[#012C4C] placeholder:text-[#012C4C]/40 focus:ring-0 focus:border-[#01889F] transition-colors"
                  required
                  disabled={isLoading}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 inset-y-0 flex items-center text-[#CED4DA] hover:text-[#012C4C] transition-colors"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
              <div className="flex justify-end pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setForgotPasswordEmail(formData.emailAddress.trim());
                    setForgotPasswordOpen(true);
                  }}
                  className="text-[13px] font-medium text-[#01889F] hover:underline"
                >
                  Forgot Password?
                </button>
              </div>
            </div>

            {error && (
              <div className="p-3 bg-red-50 border border-red-100 rounded text-sm text-red-600 font-medium">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={isLoading}
              className="w-full h-12 mt-4 bg-[#01889F] hover:bg-[#016f82] text-white font-bold text-[18px] rounded-md transition-all shadow-md active:scale-[0.98] disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Login"}
            </button>

            {/* <div className="flex items-center gap-4 py-4">
              <div className="h-[1px] flex-1 bg-[#DEE2E6]" />
              <span className="text-[12px] font-bold text-[#6C757D]">OR</span>
              <div className="h-[1px] flex-1 bg-[#DEE2E6]" />
            </div> */}

            {/* <div className="grid grid-cols-2 gap-4">
              <button type="button" className="flex items-center justify-center gap-2 h-11 border border-[#DEE2E6] rounded-md hover:bg-slate-50 transition-colors text-[13px] font-medium">
                <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" className="w-5 h-5" alt="Google" />
                Continue with Google
              </button>
              <button type="button" className="flex items-center justify-center gap-2 h-11 border border-[#DEE2E6] rounded-md hover:bg-slate-50 transition-colors text-[13px] font-medium text-black">
                <svg className="w-5 h-5" viewBox="0 0 384 512"><path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 21.8-88.5 21.8-11.4 0-51.1-20.8-83.6-20.1-42.9 .6-82.7 24.9-104.6 63.3-44.9 77.7-11.5 191.7 32 254.6 21.2 30.7 46.7 65.1 80 64.1 32.6-1.1 45.1-20.8 84.7-20.8 39.1 0 50.4 20.8 84.7 20.1 34.3-.6 56.4-31 77.4-61.6 24.4-35.4 34.4-69.7 34.9-71.3-.8-.3-67.4-25.9-67.6-102.7zm-60.6-209.1C275.6 37.8 291 16.5 291.7 0c-16 .7-34.3 10.7-45.7 24-11.4 13.4-21.2 35.5-19.1 51.1 17.1 .7 34 -8.8 31.4 -15.5z" /></svg>
                Continue with Apple
              </button>
            </div> */}
          </form>

          {/* <p className="mt-12 text-[15px] font-medium text-[#7D7D7D]">
            Don't have an account yet? <button type="button" className="text-[#012C4C] font-bold hover:underline">Sign Up</button>
          </p> */}
        </div>
      </div>

      <Dialog open={forgotPasswordOpen} onOpenChange={setForgotPasswordOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Forgot Password</DialogTitle>
            <DialogDescription>
              Enter your email address and we&apos;ll send you a password reset link.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleForgotPassword} className="space-y-4">
            <div className="space-y-2 text-left">
              <label className="text-[14px] font-semibold text-[#012C4C]">
                Email Address
              </label>
              <input
                type="email"
                placeholder="Enter your email address"
                value={forgotPasswordEmail}
                onChange={(e) => setForgotPasswordEmail(e.target.value)}
                className="w-full h-12 px-4 bg-white border border-[#012C4C] rounded-md text-[16px] text-[#012C4C] placeholder:text-[#012C4C]/40 focus:ring-0 focus:border-[#01889F] transition-colors"
                required
                disabled={isResetLoading}
              />
            </div>

            <button
              type="submit"
              disabled={isResetLoading}
              className="w-full h-12 bg-[#01889F] hover:bg-[#016f82] text-white font-bold text-[16px] rounded-md transition-all shadow-md active:scale-[0.98] disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isResetLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Send Reset Link"}
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
